import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { f as switchOrder, d as SortIcon, e as SortOrder } from './index2.js';
import { T as TrackingService } from './index3.js';
import { r as remCalc } from './index5.js';
import { d as defineCustomElement$1 } from './roche-icon2.js';

var TableSortHeadersAppearance;
(function (TableSortHeadersAppearance) {
    TableSortHeadersAppearance["DEFAULT"] = "default";
    TableSortHeadersAppearance["PIPELINE"] = "pipeline";
    TableSortHeadersAppearance["DIAGNOSTICS"] = "diagnostics";
})(TableSortHeadersAppearance || (TableSortHeadersAppearance = {}));
var TableSortHeaderType;
(function (TableSortHeaderType) {
    TableSortHeaderType["DEFAULT"] = "default";
    TableSortHeaderType["PIPELINE"] = "pipeline";
})(TableSortHeaderType || (TableSortHeaderType = {}));

const rocheTableSortHeaderCss = ":host{width:100%}.table>div{font-family:\"roche-sans\";width:100%;margin-top:1.5rem;overflow-x:auto;display:block}.table__header{background:transparent;position:relative;display:grid}@media (min-width: 20rem){.table__header{grid-template-columns:1fr 1fr}}@media (min-width: 48rem){.table__header{grid-template-columns:25% 20% 15% 15% 15% 10%}}.table__headline{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--button--font-size--small-up);line-height:1.3125rem;vertical-align:bottom;text-transform:capitalize}@media (min-width: 48rem){.table__headline{font-size:var(--button--font-size--medium-up);line-height:1.4375rem}}.table__headline--sortable{position:relative;}.table__headline--sortable:hover roche-icon{fill:var(--color-interactive--primary-hover)}.table__headline--sortable__item{cursor:pointer}.table__headline--sortable roche-icon{position:absolute;right:0.625rem;bottom:1.4375rem;fill:var(--color-interactive--primary-idle)}.table__headline--sortable roche-icon:hover{fill:var(--color-interactive--primary-hover)}roche-table-sort-header[appearance=pipeline] .table__headline{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--footnotes--font-size--small-up);line-height:1.1875rem;vertical-align:top;padding:1rem calc(1rem / 2);color:#544f4f;border-bottom:1px solid #eae8e5;}@media (min-width: 48rem){roche-table-sort-header[appearance=pipeline] .table__headline{font-size:var(--footnotes--font-size--medium-up)}}roche-table-sort-header[appearance=pipeline] .table__headline--sortable span{display:flex}roche-table-sort-header[appearance=pipeline] .table__headline--sortable__item{float:left;clear:left;white-space:pre}roche-table-sort-header[appearance=pipeline] .table__headline--sortable roche-icon{bottom:2.875rem;padding:4px 0 0 4px;position:unset}@media (min-width: 20rem){roche-table-sort-header[appearance=pipeline] .table__headline--sortable:last-of-type{display:flex;align-items:center;justify-content:flex-end;padding-right:1rem}}@media (min-width: 48rem){roche-table-sort-header[appearance=pipeline] .table__headline--sortable:last-of-type{display:block}}roche-table-sort-header[appearance=pipeline] .table__headline--sortable:first-of-type{padding:1rem 0 1rem 0}roche-table-sort-header[appearance=pipeline] .table__headline--sortable:first-of-type .table__headline--sortable__item:last-child roche-icon{bottom:1.4375rem}@media (min-width: 48rem){roche-table-sort-header[appearance=pipeline] .table__headline--sortable:nth-child(2){padding-left:1rem}}roche-table-sort-header[appearance=diagnostics] .table__header{background:transparent;position:relative;display:grid}@media (min-width: 20rem){roche-table-sort-header[appearance=diagnostics] .table__header{grid-template-columns:1fr 1fr}}@media (min-width: 64rem){roche-table-sort-header[appearance=diagnostics] .table__header{grid-template-columns:15% 15% 60% 10%}}roche-table-sort-header[appearance=diagnostics] .table__headline{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--footnotes--font-size--small-up);line-height:1.1875rem;vertical-align:top;padding:1rem calc(1rem / 2) 1rem calc(1rem * 2);color:#544f4f;border-bottom:1px solid #eae8e5;}@media (min-width: 48rem){roche-table-sort-header[appearance=diagnostics] .table__headline{font-size:var(--footnotes--font-size--medium-up)}}roche-table-sort-header[appearance=diagnostics] .table__headline--sortable__item{cursor:default}@media (min-width: 20rem){roche-table-sort-header[appearance=diagnostics] .table__headline:last-of-type{display:flex;align-items:center;justify-content:flex-end}}@media (min-width: 1024px){roche-table-sort-header[appearance=diagnostics] .table__headline:last-of-type{display:block}}roche-table-sort-header[appearance=diagnostics] .table__headline:first-of-type{padding:1rem 0 1rem 0}";
const RocheTableSortHeaderStyle0 = rocheTableSortHeaderCss;

const RocheTableSortHeader = /*@__PURE__*/ proxyCustomElement(class RocheTableSortHeader extends HTMLElement {
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            event: 'table_sort_functions',
            component_name: 'roche-table-sort-header',
        };
    }
    get headersParsed() {
        if (this.headers instanceof Array) {
            return this.headers;
        }
        return this.headers ? JSON.parse(this.headers) : [];
    }
    constructor() {
        super();
        this.__registerHost();
        this.sortEvent = createEvent(this, "rocheTableSortHeader", 7);
        this.headers = undefined;
        this.sortable = true;
        this.appearance = TableSortHeadersAppearance.DEFAULT;
        this.type = TableSortHeaderType.DEFAULT;
        this.sortBy = undefined;
        this.sortOrder = undefined;
        this.getHeaderCell = this.getHeaderCell.bind(this);
        this.getHeaderInner = this.getHeaderInner.bind(this);
    }
    render() {
        return (h(Host, { key: '39fb9ac68ac8f32b8b1a3f22d2325557d98e2ebb' }, h("div", { key: '3b1364a1390c736ecd077482acc4f52144a2655d', class: "table__header" }, this.headersParsed.map(this.getHeaderCell))));
    }
    handleSort(clickedHeader) {
        if (!this.sortable) {
            return undefined;
        }
        return () => {
            this.updateSortBy(clickedHeader);
        };
    }
    updateSortBy(index) {
        this.sortOrder = switchOrder(index !== undefined && index === this.sortBy ? this.sortOrder : undefined);
        this.sortBy = index;
        this.sortEvent.emit(this);
        if (this.type !== TableSortHeaderType.PIPELINE) {
            TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { event: 'sort', value: this.sortOrder, additionalParameters: {
                    sortedColumn: this.sortBy,
                } }));
        }
    }
    updateSortIcon(index) {
        if (!this.sortOrder) {
            return SortIcon.DESCENDING;
        }
        return this.sortOrder === SortOrder.ASCENDING && this.sortBy === index
            ? SortIcon.ASCENDING
            : SortIcon.DESCENDING;
    }
    getHeaderCell(tableHead, index, elements) {
        if (this.type === TableSortHeaderType.PIPELINE && index === 1) {
            return null;
        }
        return h("div", { class: { table__headline: true, 'table__headline--sortable': this.sortable }, key: `table-large-header-${index}` }, this.getHeaderInner(tableHead, index), this.type === TableSortHeaderType.PIPELINE && index === 0
            && this.getHeaderInner(elements[1], 1));
    }
    getHeaderInner(tableHead, index) {
        return h("span", { class: "table__headline--sortable__item", onClick: this.handleSort(index) }, tableHead, this.sortable ? h("roche-icon", { "data-sort-icon": this.updateSortIcon(index), icon: this.updateSortIcon(index), height: `${remCalc(12)}rem` }) : '');
    }
    static get watchers() { return {
        "sortBy": ["updateSortIcon"]
    }; }
    static get style() { return RocheTableSortHeaderStyle0; }
}, [0, "roche-table-sort-header", {
        "headers": [1],
        "sortable": [4],
        "appearance": [513],
        "type": [1],
        "sortBy": [32],
        "sortOrder": [32]
    }, undefined, {
        "sortBy": ["updateSortIcon"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-table-sort-header", "roche-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-table-sort-header":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheTableSortHeader);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

export { RocheTableSortHeader as R, TableSortHeadersAppearance as T, TableSortHeaderType as a, defineCustomElement as d };

//# sourceMappingURL=roche-table-sort-header2.js.map